<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-40"
    @click="$emit('close')"
  >
    <!-- Backdrop -->
    <div class="absolute inset-0 top-[64px] bg-black/25"></div>
    
    <!-- Panel -->
    <div
      class="absolute top-16 right-4 w-96 max-w-[calc(100vw-2rem)] n-raised rounded-(--nviya-radius-lg) border max-h-96 flex flex-col"
      data-notification-panel
      @click.stop
    >
      <!-- Header -->
      <div class="p-4 border-b n-divider">
        <div class="flex items-center justify-between">
          <h3 class="typo-section-title n-heading">Notifications</h3>
          <div class="flex items-center space-x-2">
            <button
              v-if="notifications.length > 0"
              @click="markAllAsRead"
              class="text-sm text-blue-600 hover:text-blue-700"
            >
              Mark all read
            </button>
            <button
              @click="$emit('close')"
              class="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <FontAwesome :icon="['fas', 'times']" class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      
      <!-- Notifications List -->
      <div class="flex-1 overflow-y-auto">
        <div v-if="notifications.length === 0" class="p-8 text-center">
          <div class="w-12 h-12 bg-(--nviya-figure-bg) rounded-full flex items-center justify-center mx-auto mb-4">
            <FontAwesome :icon="['fas', 'bell']" class="w-6 h-6 text-gray-400" />
          </div>
          <p class="text-gray-500 text-sm">No notifications yet</p>
        </div>

        <div v-else class="divide-y divide-(--nviya-border)">
          <div
            v-for="notification in sortedNotifications"
            :key="notification.id"
            class="p-4 hover:bg-(--nviya-figure-bg) transition-colors cursor-pointer"
            :class="{ 'bg-blue-50': !notification.read }"
            @click="handleNotificationClick(notification)"
          >
            <div class="flex items-start space-x-3">
              <div class="flex-shrink-0">
                <div
                  class="w-2 h-2 rounded-full mt-2"
                  :class="getUrgencyColor(notification.urgency)"
                ></div>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center space-x-2">
                  <h4
                    class="text-sm font-medium n-heading"
                    :class="{ 'font-semibold': !notification.read }"
                  >
                    {{ notification.companyName }}
                  </h4>
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                    :class="getUrgencyBadgeClass(notification.urgency)"
                  >
                    {{ notification.urgency }}
                  </span>
                </div>
                <p class="mt-1 text-sm text-gray-600">{{ labelMappingStore.getLabel(notification.exposureType) }}</p>
                <p class="mt-1 text-xs text-gray-500 line-clamp-2">{{ notification.summary }}</p>
                <div class="mt-1 flex items-center justify-between">
                  <p class="text-xs text-gray-400">{{ formatTime(notification.timestamp) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Footer -->
      <div v-if="notifications.length > 0" class="p-4 border-t n-divider">
        <button
          @click="clearAll"
          class="w-full text-sm text-red-600 hover:text-red-700 py-2"
        >
          Clear all notifications
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  notifications: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'markAsRead', 'markAllAsRead', 'clearAll', 'navigate'])

const router = useRouter()
const { createSession } = useChat()

// Import Label Mapping store
import { useLabelMappingStore } from '@/store/useLabelMappingStore'

// Use the global label mapping store
const labelMappingStore = useLabelMappingStore()

// Use the notifications passed as props instead of creating our own
const notifications = computed(() => props.notifications)

// Get urgency color for the dot indicator
const getUrgencyColor = (urgency) => {
  switch (urgency) {
    case 'Actionable Event':
      return 'bg-red-500'
    case 'Material Event':
      return 'bg-blue-500'
    case 'False Alert':
      return 'bg-gray-400'
    default:
      return 'bg-gray-300'
  }
}

// Get urgency badge class
const getUrgencyBadgeClass = (urgency) => {
  switch (urgency) {
    case 'Actionable Event':
      return 'bg-red-100 text-red-800'
    case 'Material Event':
      return 'bg-blue-100 text-blue-800'
    case 'False Alert':
      return 'bg-(--nviya-figure-bg) text-(--nviya-text-primary)'
    default:
      return 'bg-(--nviya-figure-bg) text-(--nviya-text-primary)'
  }
}

const sortedNotifications = computed(() => {
  return [...notifications.value].sort((a, b) =>
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  )
})

const handleNotificationClick = async (notification) => {
  emit('markAsRead', notification.id)

  try {
    
    // Store the predefined message in localStorage (similar to index.vue)
    
    // Create a session for this notification
    const session = await createSession({
      notificationId: notification.id
    })
    
    if (session) {
      // If this is an existing session (not new), don't send the predefined message
      if (session.is_new === true) {
        // Generate predefined message using notification data
        const predefinedMessage = `Share a short summary of the Impact Analysis for ${notification.companyName} with Event Category Type = ${labelMappingStore.formatExposureType(notification.exposureType)}.`

        localStorage.setItem('pendingChatMessage', predefinedMessage)
      }

      // Close the notification panel
      emit('close')
      // Navigate to the new session
      await router.push(`/chat/${session.id}`)
    }
  } catch (error) {
    console.error('Failed to create session for notification:', error)
  }
}

const markAsRead = (id) => {
  emit('markAsRead', id)
}

const markAllAsRead = () => {
  emit('markAllAsRead')
}

const clearAll = () => {
  emit('clearAll')
}

// Format timestamp to relative time
const formatTime = (timestamp) => {
  const now = new Date()
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

// Close panel on escape key
const handleKeydown = (event) => {
  if (event.key === 'Escape' && props.isOpen) {
    emit('close')
  }
}

// Prevent background scrolling when panel is open, but allow panel scrolling
const preventBackgroundScroll = (e) => {
  // Only prevent scroll if the event target is not within the notification panel
  const notificationPanel = document.querySelector('[data-notification-panel]')
  if (notificationPanel && !notificationPanel.contains(e.target)) {
    e.preventDefault()
  }
}

watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    // Prevent background scrolling when panel opens, but allow panel scrolling
    document.body.style.overflow = 'hidden'
    document.addEventListener('wheel', preventBackgroundScroll, { passive: false })
    document.addEventListener('touchmove', preventBackgroundScroll, { passive: false })
  } else {
    // Restore scrolling when panel closes
    document.body.style.overflow = ''
    document.removeEventListener('wheel', preventBackgroundScroll)
    document.removeEventListener('touchmove', preventBackgroundScroll)
  }
})

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  // Clean up scroll prevention on unmount
  document.body.style.overflow = ''
  document.removeEventListener('wheel', preventBackgroundScroll)
  document.removeEventListener('touchmove', preventBackgroundScroll)
})
</script>
